import React, { useEffect, useMemo, useState } from 'react'
import useSWR from 'swr';
import { Button, Card, CardBody, CardHeader, Divider, Spinner, TableCell, TableRow } from '@nextui-org/react';
import { format } from 'date-fns';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import useAuth from '../../../../hooks/useAuth';
import ModalDetailPreviewTransport from '../../../../components/fragments/admin/report/preview/ModalDetailPreviewTransport';
import ModalFinalizeReport from '../../../../components/fragments/admin/report/preview/ModalFinalizeReport';
import ModalReportReview from '../../../../components/fragments/admin/report/preview/ModalReportReview';
import { formattedDate, groupedTransportDataByVehicle, hasValidRole, hasValidStatus } from '../../../../services/helpers';
import StatusPengajuanPelaporan from '../../../../enums/statusPengajuanPelaporan';
import FilterReactSelect from '../../../../components/elements/FilterReactSelect';
import ClientTablePagination from '../../../../components/elements/ClientTablePagination';
import { getFetcher, getPdfUrl } from '../../../../services/api';
import RolesAccess from '../../../../enums/roles';
import useListCompany from '../../../../hooks/useListCompany';
import { useParams } from 'react-router-dom';
import RootAdmin from '../../../../components/layouts/RootAdmin';

export default function TableReportTransportReview({ isPreviewOnly = false }) {
    const { companyId, periodId } = useParams();
    const {roles} = useAuth();
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const { data, isLoading, mutate } = useSWR(
        companyId && periodId
            ? `/api/period/preview-pelaporan-pengangkutan?companyId=${companyId}&periodId=${periodId}`
            : null, 
        getFetcher
    )
    const { dataCompany, isLoadingCompany } = useListCompany()
    const { data: dataPeriod, isLoading: isLoadingPeriod } = useSWR(`/api/period/all`, getFetcher);
    const groupedDataVehicle = data ? groupedTransportDataByVehicle(data, isLoading) : [];

    // Pesan "tidak ada data" dari backend ditampilkan di sini, bukan di dalam
    // groupedTransportDataByVehicle yang berjalan saat render.
    useEffect(() => {
      if (data?.message) toast.error(data.message);
    }, [data?.message]);
    
    useEffect(() => {
        if (dataPeriod?.length > 0 && periodId && isLoadingPeriod === false) {
            const activePeriod = dataPeriod?.find((item) => item?.id === periodId);
            if (activePeriod) {
                setSelectedPeriod({ label: activePeriod.name, value: activePeriod.id });
            }
        }
        if(dataCompany?.data?.length > 0 && companyId && isLoadingCompany === false) {
            const company = dataCompany?.data?.find((item) => item?.id === companyId);
            if (company) {
                setSelectedCompany({ label: company.name, value: company.id });
            }
        }
    }, [dataPeriod, dataCompany]);    

    const header = ['Kendaraan', 'Keterangan', 'Total Pengangkutan', 'Aksi'];
    const content = (item, index) => (
        <TableRow key={index}>
            <TableCell>{item.vehicle.noPolisi} - {item.vehicle.modelKendaraan}</TableCell>
            <TableCell>Melakukan {item.bahanB3.length}x Pengangkutan</TableCell>
            <TableCell>{item.totalJumlahB3} Kg</TableCell>
            <TableCell><ModalDetailPreviewTransport item={item.bahanB3} /></TableCell>
        </TableRow>
    )

    async function openPdfDirectly(endpoint) {
        try {
            await getPdfUrl(endpoint)
        } catch (error) {            
            toast.error('Failed to open PDF');
        }
    };

    return (
        <RootAdmin>
                    <>
            {
                isLoading || isLoadingCompany || selectedCompany === null || selectedPeriod === null || isLoadingPeriod
                ?
                <div className="flex items-center justify-center mt-3">
                  
                </div>
                :
                <div className='w-full flex items-center justify-end gap-2'>
                    {hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PENGELOLA]) && hasValidStatus(data?.statusPelaporan, [StatusPengajuanPelaporan.DRAFT, null, undefined]) &&(
                        <ModalFinalizeReport 
                            endpoint="/api/period/finalize-pelaporan-pengangkutan"
                            mutate={mutate} 
                            selectedCompany={selectedCompany}
                            selectedPeriod={selectedPeriod}
                            setSelectedCompany={setSelectedCompany} 
                            setSelectedPeriod={setSelectedPeriod}
                        />
                    )}
                    {hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_PELAPORAN]) && hasValidStatus(data?.statusPelaporan, [StatusPengajuanPelaporan.MENUNGGU_PERSETUJUAN]) && (
                        <ModalReportReview endpoint="/api/period/review-pelaporan-pengangkutan" 
                            mutate={mutate}
                            selectedCompany={selectedCompany}
                            selectedPeriod={selectedPeriod}
                            setSelectedCompany={setSelectedCompany} 
                            setSelectedPeriod={setSelectedPeriod}
                        />
                    )}
                </div>
            }
            {
                isLoading || isLoadingCompany || selectedCompany === null || selectedPeriod === null || isLoadingPeriod
                ?
                <div className="flex items-center justify-center mt-3">
                    <Spinner/>
                </div>
                :
                <div className='grid grid-cols-4 gap-3 mt-3'>
                    <div className='col-span-1 flex flex-col gap-3'>
                        <Card radius='sm' className='col-span-1'>
                            <CardHeader>Perusahaan</CardHeader>
                            <Divider/>
                            <CardBody className='flex flex-col gap-3'>
                                <div className='flex flex-col'>
                                    <span className='text-xs text-ink-400 uppercase'>Nama Perusahaan</span>
                                    <span className='text-sm font-medium'>{data?.company?.name || '-'}</span>
                                </div>
                                <div className='flex flex-col'>
                                    <span className='text-xs text-ink-400 uppercase'>Alamat</span>
                                    <span className='text-sm font-medium'>{data?.company?.alamatKantor || '-'}</span>
                                </div>
                                <div className='flex flex-col'>
                                    <span className='text-xs text-ink-400 uppercase'>Tipe Perusahaan</span>
                                    <span className='text-sm font-medium'>{data?.company?.tipePerusahaan?.map((item) => item?.name).join(', ') || '-'}</span>
                                </div>
                                <div className='flex flex-col'>
                                    <span className='text-xs text-ink-400 uppercase'>Email Perusahaan</span>
                                    <span className='text-sm font-medium'>{data?.company?.emailKantor || '-'}</span>
                                </div>
                                <div className='flex flex-col'>
                                    <span className='text-xs text-ink-400 uppercase'>Fax Perusahaan</span>
                                    <span className='text-sm font-medium'>{data?.company?.faxKantor || '-'}</span>
                                </div>
                            </CardBody>
                        </Card>
                        <Card radius='sm' className='col-span-1'>
                            <CardHeader>Periode</CardHeader>
                            <Divider/>
                            <CardBody className='flex flex-col gap-3'>
                                <div className='flex flex-col'>
                                    <span className='text-xs text-ink-400 uppercase'>Periode</span>
                                    <span className='text-sm font-medium'>{data?.period?.name || '-'}</span>
                                </div>
                                <div className='flex flex-col'>
                                    <span className='text-xs text-ink-400 uppercase'>Tanggal Mulai Periode</span>
                                    <span className='text-sm font-medium'>{data?.period?.startPeriodDate ? formattedDate(data?.period?.startPeriodDate) : '-'}</span>
                                </div>
                                <div className='flex flex-col'>
                                    <span className='text-xs text-ink-400 uppercase'>Tanggal Akhir Periode</span>
                                    <span className='text-sm font-medium'>{data?.period?.endPeriodDate ? formattedDate(data?.period?.endPeriodDate) : '-'}</span>
                                </div>
                                <div className='flex flex-col'>
                                    <span className='text-xs text-ink-400 uppercase'>Tanggal Akhir Finalisasi</span>
                                    <span className='text-sm font-medium'>{data?.period?.finalizationDeadline ? formattedDate(data?.period?.finalizationDeadline) : '-'}</span>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                    <Card radius='sm' className='col-span-3'>
                        <CardHeader>Preview Laporan Pengangkutan</CardHeader>
                        <Divider/>
                        <CardBody>
                            <div className="w-full flex justify-end items-center gap-2 mb-3">
                                <div className="w-2/4 flex flex-col justify-end">
                                    <div className='flex items-center gap-2'>
                                        {companyId && periodId 
                                            && hasValidStatus(data?.statusPelaporan, [StatusPengajuanPelaporan.DRAFT, StatusPengajuanPelaporan.MENUNGGU_PERSETUJUAN, null, undefined])
                                            ? <Button 
                                                size="sm" 
                                                color='primary' 
                                                onPress={() => openPdfDirectly(`/api/pdf/generateBuktiLaporPengangkutan/${companyId}/${periodId}`)} 
                                                startContent={<ArrowDownTrayIcon className="size-4" />}
                                            >
                                                Bukti Laporan
                                            </Button>
                                            : ''
                                        }
                                        {companyId && periodId && data?.statusPelaporan === StatusPengajuanPelaporan.DISETUJUI
                                            ? <Button 
                                                size="sm" 
                                                color='primary' 
                                                startContent={<ArrowDownTrayIcon className="size-4" />}
                                                onPress={() => openPdfDirectly(`/api/pdf/generateTandaTerimaPengangkutan/${companyId}/${periodId}`)}
                                            >
                                                Tanda Terima
                                            </Button>
                                            : ''
                                        }
                                    </div>
                                </div>
                            </div>
                            <ClientTablePagination header={header} content={content} isLoading={isLoading} data={groupedDataVehicle} />   
                        </CardBody>
                    </Card>
                </div>
            }
        </>
        </RootAdmin>
    )
}
